import { useState, useEffect, useCallback } from "react";
import { InputAdornment, Paper, TextField, TablePagination, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import DataTable from "components/utils/DataTable";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as DeleteIcon } from "images/svg/trash.svg";
import BackIcon from "images/svg/back-icon.svg";
import { ROUTES } from "const";
import Info from "components/utils/Alert/Info";
import toast from "react-hot-toast";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as PurchaseHelper from "helpers/PurchaseHelper";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";

function DraftPurchasePage() {
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rowToDelete, setRowToDelete] = useState(null);

  const navigate = useNavigate();

  const fetchDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const draftPurchases = await PurchaseHelper.fetchDraftPurchases();
      setDrafts(draftPurchases);
    } catch (error) {
      console.error("Failed to fetch drafts", error);
      toast.error("Failed to load drafts from local database");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleDeleteClick = (e, row) => {
    e.stopPropagation();
    setRowToDelete(row);
  };

  const handleConfirmDelete = async (row) => {
    if (!row) return;
    try {
      await PurchaseHelper.deleteDraftPurchase(row.local_id);
      toast.success("Draft deleted");
      fetchDrafts();
    } catch (error) {
      console.error("Failed to delete draft", error);
      toast.error("Failed to delete draft");
    }
  };

  const handleRowClick = (row) => {
    navigate(ROUTES.EDIT_DRAFT_PURCHASE.replace(":draftId", row.local_id));
  };

  const handleSearch = (value) => {
    setFilterText(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDrafts = drafts.filter((d) => {
    const searchStr = filterText.toLowerCase();
    const dateStr = (DateTimeHelpers.getDate(d.date) || "").toLowerCase();
    return (
      dateStr.includes(searchStr) ||
      (d.invoiceNo || "").toLowerCase().includes(searchStr) ||
      (d.vendor?.name || "").toLowerCase().includes(searchStr)
    );
  });

  const paginatedDrafts = filteredDrafts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const headerData = [
    {
      label: "Date",
      id: "date",
      type: "callback",
      viewRender: (row) => DateTimeHelpers.getDate(row.date) || "-",
    },
    {
      label: "Invoice No",
      id: "invoiceNo",
      type: "text",
    },
    {
      label: "Vendor",
      id: "vendor.name",
      type: "text",
    },
    {
      label: "Items",
      id: "items",
      type: "callback",
      viewRender: (row) => row.items?.length || 0,
    },
    {
      label: "Total Amount",
      id: "total_amount",
      type: "callback",
      viewRender: (row) => {
        const total = (row.items || []).reduce((acc, item) => acc + (item.total || 0), 0);
        return NumberFormatHelper.customerAmountFormat(total);
      },
      align: "right",
    },
    {
      label: "Actions",
      id: "action",
      type: "callback",
      viewRender: (row) => (
        <IconButton size="small" onClick={(e) => handleDeleteClick(e, row)} style={{ color: "#E50218" }}>
          <DeleteIcon style={{ width: 18, height: 18 }} />
        </IconButton>
      ),
    },
  ];

  return (
    <div style={{ padding: "10px 10px 0 10px", height: "100%", boxSizing: "border-box" }}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.leftHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(ROUTES.PURCHASE)}
              aria-label="Back to Purchase"
            >
              <img src={BackIcon} alt="Back to Purchase" />
            </button>
            <div className={styles.header}>
              <h2>Draft Purchases</h2>
              <h5>Manage and complete your unsaved purchases</h5>
            </div>
          </div>

          <div className={styles.contentWrapper}>
            <TextField
              placeholder="Search"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <div className={styles.tableContainer}>
          {isLoading ? (
            <SkeletonLoading isOpen={true} count={30} height={40} xs={12} sm={6} md={4} lg={4} />
          ) : filteredDrafts && filteredDrafts.length > 0 ? (
            <Paper
              elevation={0}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div style={{ flex: 1, overflowY: "auto" }}>
                <DataTable columns={headerData} rows={paginatedDrafts} rowClickListener={handleRowClick} />
              </div>

              <div className={styles.pagination}>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={filteredDrafts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </Paper>
          ) : (
            <Info title="No drafts to list" content="You have no draft purchases." />
          )}
        </div>
      </div>

      <ConfirmPopup
        data={rowToDelete}
        handleClose={() => setRowToDelete(null)}
        handleOkay={handleConfirmDelete}
        alertTitle="Delete Draft"
        alertContent="Are you sure you want to delete this draft?"
        successBtnName="Delete"
      />
    </div>
  );
}

export default DraftPurchasePage;
